import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    CrudFilters,
} from '@refinedev/core';
import {
    IRecordFilterVariables,
    IRecord,
    IUser,
} from 'interfaces/activity.interface';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Description } from '../../../components/activity/description';
import { Table, Form, DatePicker } from 'antd';
import {
    TextField,
    getDefaultSortOrder,
    useTable,
    useSelect,
} from '@refinedev/antd';
import { Heading } from '../../../components/ui/heading';
import { Button } from '../../../components/ui/button';
import StatusTagField from '../../../components/ui/status-tag';
import TablePagination from '../../../components/ui/table-pagination';
import {
    FilterControls,
    type FilterDropdownOption,
} from '../../../components/applications/filter-controls';
import type { FormInstance } from 'antd';
import styles from './list.module.scss';

export const ActivityList: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const [description, setDescription] = useState<any>(null);
    const [modal, setModal] = useState<boolean>(false);

    const {
        tableProps,
        searchFormProps,
        sorter,
        tableQueryResult,
        setFilters,
        current,
        setCurrent,
        pageSize,
        setPageSize,
    } = useTable<IRecord, HttpError, IRecordFilterVariables>({
        filters: {
            defaultBehavior: 'replace',
        },
        onSearch: params => {
            const filters: CrudFilters = [];
            const { user_id, query, date_range } = params;

            filters.push(
                { field: 'query', operator: 'eq', value: query },
                { field: 'user_id', operator: 'eq', value: user_id }
            );

            if (date_range && date_range.length === 2) {
                filters.push(
                    {
                        field: 'start_date',
                        operator: 'gte',
                        value: date_range[0].format('YYYY-MM-DD'),
                    },
                    {
                        field: 'end_date',
                        operator: 'lte',
                        value: date_range[1].format('YYYY-MM-DD'),
                    }
                );
            }

            return filters;
        },
    });

    const { selectProps: userSelectProps } = useSelect<IUser>({
        resource: 'dictionary/users',
        defaultValue: [],
        optionValue: 'id',
        optionLabel: 'first_name',
        pagination: { mode: 'server' },
        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],
    });

    const onShowDescription = (data: any) => {
        setModal(true);
        setDescription(data);
    };

    const memoLoader = useMemo(
        () => description === null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [modal, description]
    );

    const formInstance = searchFormProps.form as FormInstance | undefined;
    const queryValue = Form.useWatch('query', formInstance);
    const userValue = Form.useWatch('user_id', formInstance);
    const dateRangeValue = Form.useWatch('date_range', formInstance);

    const clearFilterLabel = t('applications.filters.clear', 'Очистити фільтр');

    const removeFilterByField = (fields: string | string[]) => {
        const normalized = Array.isArray(fields) ? fields : [fields];
        setFilters(prevFilters =>
            prevFilters.filter(filter => {
                if (!('field' in filter)) return true;
                return !normalized.includes(filter.field as string);
            })
        );
    };

    const handleResetFilters = () => {
        if (!formInstance) return;

        formInstance.setFieldsValue({
            query: undefined,
            user_id: undefined,
            date_range: undefined,
        });

        removeFilterByField(['query', 'user_id', 'start_date', 'end_date']);
        formInstance.submit();
    };

    const handleUserSelect = (
        value: string | number | undefined,
        option?: FilterDropdownOption
    ) => {
        if (!formInstance) return;

        formInstance.setFieldsValue({
            user_id: value,
        });

        if (value === undefined) {
            removeFilterByField('user_id');
        }

        formInstance.submit();
    };

    const handleDateRangeChange = (value: any) => {
        if (!formInstance) return;

        if (!value || value.length === 0) {
            formInstance.setFieldsValue({ date_range: undefined });
            removeFilterByField(['start_date', 'end_date']);
            formInstance.submit();
            return;
        }

        formInstance.setFieldsValue({ date_range: value });
        formInstance.submit();
    };

    const isAllActive =
        (!queryValue || queryValue === '') &&
        !userValue &&
        (!dateRangeValue || dateRangeValue.length === 0);

    const isInitialQueryWatch = useRef(true);

    useEffect(() => {
        if (!formInstance) return;

        if (isInitialQueryWatch.current) {
            isInitialQueryWatch.current = false;
            return;
        }

        const handler = window.setTimeout(() => {
            formInstance.submit();
        }, 300);

        return () => window.clearTimeout(handler);
    }, [formInstance, queryValue]);

    const { pagination: rawPagination, ...restTableProps } = tableProps;
    const pagination =
        rawPagination === false ? undefined : rawPagination ?? undefined;

    const totalItems = tableQueryResult?.data?.total ?? pagination?.total ?? 0;
    const currentPage = current ?? pagination?.current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;

        setPageSize?.(value);
        setCurrent?.(1);
    };

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.header}>
                <Heading level={1} align="center" className={styles.title}>
                    {t('activity.activity')}
                </Heading>
                <FilterControls
                    formProps={searchFormProps}
                    searchFieldName="query"
                    searchPlaceholder={t('activity.fields.query')}
                    filtersLabel={t('activity.titles.filters')}
                    clearFilterLabel={clearFilterLabel}
                    isAllActive={isAllActive}
                    onResetFilters={handleResetFilters}
                    hiddenFieldNames={['user_id', 'date_range']}
                    dropdowns={[
                        {
                            placeholder: t('activity.fields.user'),
                            value: userValue,
                            options:
                                (userSelectProps.options as FilterDropdownOption[]) ??
                                [],
                            onSelect: handleUserSelect,
                            clearLabel: clearFilterLabel,
                        },
                        {
                            placeholder: t('activity.fields.date_range'),
                            value: undefined,
                            onSelect: () => undefined,
                            clearLabel: clearFilterLabel,
                            render: (
                                <Form.Item
                                    name="date_range"
                                    className={styles.filterItem}
                                    label={false}
                                >
                                    <DatePicker.RangePicker
                                        allowClear
                                        format="DD.MM.YYYY"
                                        onChange={handleDateRangeChange}
                                        style={{ width: '100%' }}
                                        disabledDate={current =>
                                            current &&
                                            current.valueOf() > Date.now()
                                        }
                                        className={styles.datePicker}
                                        bordered={false}
                                    />
                                </Form.Item>
                            ),
                        },
                    ]}
                    inlineFilters={null}
                />
            </header>
            <div className={styles.tableWrapper}>
                <Table
                    {...restTableProps}
                    pagination={false}
                    rowKey="id"
                    className={styles.table}
                    scroll={{ x: 500 }}
                >
                    <Table.Column
                        dataIndex={['causer', 'first_name']}
                        key="causer_id"
                        title={t('activity.fields.causer')}
                        render={value => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="log_name"
                        key="log_name"
                        title={t('activity.fields.log_name')}
                        render={value => <TextField value={value} />}
                        defaultSortOrder={getDefaultSortOrder(
                            'log_name',
                            sorter
                        )}
                        sorter
                    />
                    <Table.Column
                        dataIndex="description"
                        key="descripton_text"
                        title={t('activity.fields.description')}
                        render={value => <TextField value={value} />}
                    />
                    <Table.Column
                        dataIndex="status"
                        key="status"
                        title={t('activity.fields.status')}
                        render={value =>
                            value ? <StatusTagField value={value} /> : null
                        }
                        defaultSortOrder={getDefaultSortOrder('status', sorter)}
                        sorter
                    />
                    <Table.Column
                        dataIndex="created_at"
                        key="created_at"
                        title={t('activity.fields.created_at')}
                        render={value => <TextField value={value} />}
                        defaultSortOrder={getDefaultSortOrder(
                            'created_at',
                            sorter
                        )}
                        sorter
                    />
                    <Table.Column
                        dataIndex="description"
                        key="description"
                        title={t('activity.fields.description')}
                        render={(_, row: any) => {
                            const descriptionValue = row.properties;

                            return (
                                <Button
                                    onClick={() =>
                                        onShowDescription(descriptionValue)
                                    }
                                    variant="secondary"
                                >
                                    {t('buttons.show')}
                                </Button>
                            );
                        }}
                    />
                </Table>
            </div>
            {totalItems ? (
                <TablePagination
                    currentPage={currentPage}
                    pageSize={resolvedPageSize}
                    totalItems={totalItems}
                    pageSizeOptions={orderedPageSizeOptions}
                    onChangePage={handlePageChange}
                    onChangePageSize={handlePageSizeChange}
                    showDocumentsLabel={t(
                        'applications.pagination.show_documents',
                        'Показувати документи'
                    )}
                    previousLabel={t(
                        'applications.pagination.previous',
                        'Попередня сторінка'
                    )}
                    nextLabel={t(
                        'applications.pagination.next',
                        'Наступна сторінка'
                    )}
                    rangeLabel={(start, end, total) =>
                        `${start}-${end} ${t(
                            'applications.pagination.of_total',
                            'з'
                        )} ${total}`
                    }
                    className={styles.pagination}
                />
            ) : null}

            <Description
                visible={modal}
                onCancel={() => {
                    setModal(false);
                    setDescription(null);
                }}
                description={description}
                width={'90%'}
                isLoading={memoLoader}
            />
        </div>
    );
};
